/**
 * Create a market view by copying an existing one — the `/workspace_market/clone/:uuid` route.
 *
 * A create flow that happens to start from someone else's numbers: it saves through the same
 * `create` call as {@link ./NewMarketView}, but its type and custom-ness are inherited from the
 * source rather than chosen, which is why it lives on the uuid route.
 */
import type { MarketViewDetails, MarketViewType } from "@eu.mdotm.api/sphere";
import { useCrumbs } from "$root/components/Crumbs/useCrumbs";
import { LeavePrompt } from "$root/components/LeavePrompt";
import { PageHeader } from "$root/components/PageHeader";
import { useTypedNavigation } from "$root/components/PlatformRouter/RoutesDef";
import { ReactQueryWrapperBase } from "$root/components/ReactQueryWrapper";
import { Button } from "@mdotm/shadcn/mdotm/components";
import fastDeepEqual from "fast-deep-equal";
import { useTranslation } from "react-i18next";
import {
	MARKET_VIEW_LIST_ROUTE,
	MarketViewActionsMenu,
	MarketViewEditor,
	useMarketViewScenarios,
	useMarketViewSummary,
	useMarketViewWorkspaceConfig,
	useSaveMarketView,
} from "./shared";

const CloneMarketView = ({ uuid }: { uuid: string }): React.JSX.Element => {
	const querySummary = useMarketViewSummary(uuid);

	// Unlike the read-only page, there is no partial state worth rendering here: every control on this
	// page — the crumbs, the scenario list, the save payload — is derived from the market view being
	// copied, so the page waits for it rather than showing placeholders it would immediately replace.
	return (
		<ReactQueryWrapperBase query={querySummary}>
			{(source) => <CloneMarketViewForm sourceUuid={uuid} source={source} />}
		</ReactQueryWrapperBase>
	);
};

const CloneMarketViewForm = ({
	sourceUuid,
	source,
}: {
	sourceUuid: string;
	source: MarketViewDetails;
}): React.JSX.Element => {
	const { t } = useTranslation();
	const { push } = useTypedNavigation();
	const crumbs = useCrumbs();

	const type = source.marketViewType as MarketViewType;
	const isCustom = source.custom ?? false;

	const { settings, assetClassAliases } = useMarketViewWorkspaceConfig();
	const scenario = useMarketViewScenarios({ type, isCustom, uuid: sourceUuid });
	const { pathToNotBlock, promptForNameAndSave } = useSaveMarketView({ type, isCustom, scenario });

	return (
		<>
			<PageHeader
				crumbs={[
					...crumbs.portfolioStudio("MarketViews"),
					...crumbs.duplicateMarketView({ uuid: source.uuid, name: source.name }),
				]}
				titleAction={
					<div className="flex items-center space-x-2">
						<Button
							size="small"
							onClick={() => push("PortfoliosStudio", MARKET_VIEW_LIST_ROUTE)}
							palette="tertiary"
							disabled={false}
							data-qualifier="MarketViewWorkspace/Cancel"
						>
							Cancel
						</Button>
						<Button
							size="small"
							onClick={promptForNameAndSave}
							palette="primary"
							disabled={false}
							data-qualifier="MarketViewWorkspace/Save"
						>
							Save
						</Button>
						<MarketViewActionsMenu uuid={sourceUuid} summary={source} />
					</div>
				}
			/>
			<MarketViewEditor
				type={type}
				isCustom={isCustom}
				mode="edit"
				scenario={scenario}
				settings={settings}
				assetClassAliases={assetClassAliases}
				showWidgetHeader={false}
				// A copy is pinned to the scenario it was made from.
				lockScenarioSelection
				hidePositioning
				remapCommentaryToDefaults={false}
			/>
			<LeavePrompt
				title={t("NOTIFICATION_SETTINGS.LEAVE_PROMPT.TITLE")}
				when={
					scenario.originalScenario === null
						? false
						: fastDeepEqual(scenario.originalScenario, scenario.marketScenario) === false
				}
				pathToNotBlock={pathToNotBlock}
			>
				{t("NOTIFICATION_SETTINGS.LEAVE_PROMPT.MESSAGE")}
			</LeavePrompt>
		</>
	);
};

export default CloneMarketView;
